import React, { useRef, useState } from "react";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import HeaderTitle from "../../../components/common/HeaderTitle/HeaderTitle";

import DownloadFileButton from "../../../components/Dashboard/DownloadFileButton/DownloadFileButton";
import axios from "axios";
import { API_ENDPOINT } from "../../../services/api/resources";
import { useSelector } from "react-redux";
import { jsPDF } from "jspdf";
import "jspdf-autotable";

import sampleRateContractPDF from "../../../assets/files/Sample_rate_contract.pdf";
import sampleFilterDocumentExcel from "../../../assets/files/RC_Sample_Filter.xlsx";
import {
  DOCUMENT_UPLOAD_ACCEPT,
  DOCUMENT_UPLOAD_FORMAT_LABEL,
} from "../../../utils/documentUploadTypes";
import {
  enterpriseBackButtonSx,
  enterpriseBodyTextSx,
  enterpriseBrowseButtonSx,
  enterpriseDangerButtonSx,
  enterpriseGhostButtonSx,
  enterprisePageSx,
  enterprisePrimaryButtonSx,
  enterpriseSectionTitleSx,
  enterpriseSupportingTextSx,
  getEnterpriseUploadZoneSx,
} from "../../../components/common/upload/enterpriseUploadStyles";

const FILTER_UPLOAD_ACCEPT = ".csv,.xls,.xlsx";
const FILTER_UPLOAD_FORMAT_LABEL = "CSV, XLS, XLSX";
const FILTER_UPLOAD_EXTENSIONS = ["csv", "xls", "xlsx"];

const isFilterUploadFile = (file) => {
  const extension = String(file?.name || "")
    .split(".")
    .pop()
    ?.toLowerCase();
  return FILTER_UPLOAD_EXTENSIONS.includes(extension);
};

const RcCreatePage = ({ onBack, loading, setLoading, onSave }) => {
  // ====== Refs and State ======
  const pdfInputRef = useRef(null);
  const excelInputRef = useRef(null);

  const [pdfFile, setPdfFile] = useState(null);
  const [excelFile, setExcelFile] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [showExcelPreview, setShowExcelPreview] = useState(false);

  const data = useSelector((state) => state.loginData.value);
  const [auth_token] = useState(data?.auth_token);
  const [buyer_token] = useState(data?.user?.token);

  // save
  const [, setSaveRc] = useState(null);
  const [isUploadDone, setIsUploadDone] = useState(false);

  // ====== Handlers ======
  const handleRcDrop = (e, type) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (type === "excel" && !isFilterUploadFile(file)) return;
    if (type === "pdf") setPdfFile(file);
    else setExcelFile(file);
  };

  const handleDragOver = (e) => e.preventDefault();

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (type === "excel" && !isFilterUploadFile(file)) return;
    if (type === "pdf") setPdfFile(file);
    else setExcelFile(file);
  };

  const handleClearFile = (type) => {
    if (type === "pdf") {
      setPdfFile(null);
      pdfInputRef.current.value = "";
      setShowPdfPreview(false);
    } else {
      setExcelFile(null);
      excelInputRef.current.value = "";
      setShowExcelPreview(false);
    }
  };

  // ====== API Upload ======
  const handleRCApi = async () => {
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("extract", pdfFile);
      formData.append("excel_file", excelFile);

      const response = await axios.post(
        API_ENDPOINT.extract_pdf_view,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      console.log("Upload success:", response.data);

      setExtractedData(response.data);
      setIsUploadDone(true);
    } catch (error) {
      console.error("Error uploading files:", error);
      // alert("Upload failed. Check console for details.");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);

      const formData = new FormData();
      formData.append("buyer_token", buyer_token);
      formData.append("extracted_response", JSON.stringify(extractedData || {}));

      // Send the original rate contract PDF as binary on the requested key
      if (pdfFile) {
        formData.append("reconstructed_pdf_path", pdfFile);
      }

      const res = await axios.post(API_ENDPOINT.save_extracted_rate_contract, formData, {
        headers: {
          Authorization: `Token ${auth_token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      console.log("SAVE SUCCESS:", res.data);

      onSave(res.data); // send saved RC back to RateContract.jsx so list updates
      setSaveRc(res.data);
    } catch (err) {
      console.error("SAVE ERROR:", err);
      alert("Failed to save Rate Contract");
    } finally {
      setLoading(false);
    }
  };
  const formatINR = (value) =>
    value
      ? Number(value).toLocaleString("en-IN", { minimumFractionDigits: 2 })
      : "";

  const handleDownloadPO = () => {
    if (!extractedData) {
      return;
    }

    try {
      // Create new PDF document
      const doc = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      let yPosition = 20;

      // Add title
      doc.setFontSize(16);
      doc.setFont(undefined, "bold");
      doc.text("Purchase Order Details", 14, yPosition);
      yPosition += 10;

      // Add header information
      doc.setFontSize(11);
      doc.setFont(undefined, "normal");
      doc.text(
        `PO No: ${extractedData.header_info?.purchase_order_no || ""}`,
        14,
        yPosition
      );
      yPosition += 7;
      doc.text(
        `PO Date: ${extractedData.header_info?.purchase_order_date || ""}`,
        14,
        yPosition
      );
      yPosition += 7;
      doc.text(
        `Supplier: ${
          extractedData.header_info?.vendor_details?.split("\n")[0] || ""
        }`,
        14,
        yPosition
      );
      yPosition += 10;

      // Prepare table data
      const headers = [
        "Part No",
        "Description of Goods",
        "HSN Code",
        "Quantity.",
        "Unit of Measure",
        "Rate",
        "CGST Rate",
        "SGST Rate",
        "IGST Rate",
        "Amount",
      ];

      const tableData = [];
      if (extractedData.data && Array.isArray(extractedData.data)) {
        extractedData.data.forEach((item) => {
          tableData.push([
            item["Part No"] || "",
            item["Description of Goods"] || "",
            item["HSN Code"] || "",
            item["Qty."] || "",
            item["UOM"] || "",
            item["Rate"] || "",
            item["CGST Rate"] || "",
            item["SGST Rate"] || "",
            item["IGST Rate"] || "",
            item["Amount"] || "",
          ]);
        });
      }

      // Add table using autoTable
      doc.autoTable({
        head: [headers],
        body: tableData,
        startY: yPosition,
        theme: "grid",
        headStyles: {
          fillColor: [59, 134, 199], // #3B86C7
          textColor: [255, 255, 255],
          fontStyle: "bold",
          fontSize: 9,
        },
        bodyStyles: {
          fontSize: 8,
        },
        styles: {
          overflow: "linebreak",
          cellWidth: "wrap",
        },
        columnStyles: {
          0: { cellWidth: 20 }, // Part No
          1: { cellWidth: 40 }, // Description
          2: { cellWidth: 25 }, // HSN Code
          3: { cellWidth: 15 }, // Quantity
          4: { cellWidth: 15 }, // UOM
          5: { cellWidth: 20 }, // Rate
          6: { cellWidth: 18 }, // CGST Rate
          7: { cellWidth: 18 }, // SGST Rate
          8: { cellWidth: 18 }, // IGST Rate
          9: { cellWidth: 20 }, // Amount
        },
        margin: { left: 14, right: 14 },
      });

      // Get final Y position after table
      const finalY = doc.lastAutoTable.finalY || yPosition + 50;

      // Add totals section
      doc.setFontSize(11);
      doc.setFont(undefined, "bold");
      doc.text("Summary", 14, finalY + 15);

      doc.setFont(undefined, "normal");
      doc.text(
        `Total: Rs.${formatINR(extractedData.totals?.total) || ""}`,
        14,
        finalY + 22
      );
      doc.text(
        `Taxes Total: Rs.${formatINR(extractedData.totals?.taxes_total) || ""}`,
        14,
        finalY + 29
      );
      doc.setFont(undefined, "bold");
      doc.text(
        `Grand Total: Rs.${formatINR(extractedData.totals?.grand_total) || ""}`,
        14,
        finalY + 36
      );

      // Save PDF
      const fileName = `PO_${
        extractedData.header_info?.purchase_order_no || "data"
      }_${new Date().toISOString().split("T")[0]}.pdf`;
      doc.save(fileName);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Failed to download PO data. Please try again.");
    }
  };

  const isUploadDisabled = !pdfFile || !excelFile || loading;

  return (
    <Box sx={enterprisePageSx}>
      {/* ===== Header ===== */}
      <Box
        sx={{
          mt: 1,
          mb: 2,
          ml: -1,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <HeaderTitle titleText="Create Rate Contract" />
        <Button
          variant="contained"
          size="small"
          sx={enterpriseBackButtonSx}
          onClick={onBack}
        >
          Back
        </Button>
      </Box>

      {/* ===== Upload Boxes ===== */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 4,
          justifyContent: "space-between",
        }}
      >
        {/* --- PDF Upload --- */}
        <Box
          sx={{
            flex: "1 1 420px",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Typography sx={enterpriseSectionTitleSx}>
            Rate Contract 
          </Typography>
          <Box
            onDrop={(e) => handleRcDrop(e, "pdf")}
            onDragOver={handleDragOver}
            sx={getEnterpriseUploadZoneSx(false)}
          >
            <Typography sx={{ ...enterpriseBodyTextSx, mb: 0.5, wordBreak: "break-all" }}>
              {pdfFile ? pdfFile.name : "Drag and drop file here"}
            </Typography>
            <Typography sx={enterpriseSupportingTextSx}>
              (Supported: {DOCUMENT_UPLOAD_FORMAT_LABEL})
            </Typography>

            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
              <Button
                variant="contained"
                size="medium"
                sx={enterpriseBrowseButtonSx}
                onClick={() => pdfInputRef.current.click()}
              >
                Browse
              </Button>

              {pdfFile && (
                <>
                  <Button
                    variant="outlined"
                    size="medium"
                    sx={enterpriseGhostButtonSx}
                    onClick={() => setShowPdfPreview((prev) => !prev)}
                  >
                    {showPdfPreview ? "Hide Preview" : "Preview"}
                  </Button>

                  <Button
                    variant="outlined"
                    size="medium"
                    sx={enterpriseDangerButtonSx}
                    onClick={() => handleClearFile("pdf")}
                  >
                    Clear
                  </Button>
                </>
              )}
            </Box>

            <input
              type="file"
              accept={DOCUMENT_UPLOAD_ACCEPT}
              ref={pdfInputRef}
              style={{ display: "none" }}
              onChange={(e) => handleFileChange(e, "pdf")}
            />

            {/* === PDF Preview Section === */}
            {showPdfPreview && pdfFile && (
              <Box
                sx={{
                  mt: 2,
                  p: 1,
                  borderRadius: 2,
                  border: "1px solid #e2e8f0",
                  backgroundColor: "white",
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: "#075b97",
                    fontWeight: 600,
                    mb: 1,
                    fontSize: 14,
                    textAlign: "left",
                  }}
                >
                  PDF Preview:
                </Typography>

                <iframe
                  src={URL.createObjectURL(pdfFile)}
                  title="PDF Preview"
                  width="100%"
                  height="250px"
                  style={{
                    borderRadius: "8px",
                    border: "1px solid #e2e8f0",
                  }}
                />
              </Box>
            )}
          </Box>
          {/* Download Sample PDF */}
         
        </Box>

        {/* --- Excel Upload --- */}
        <Box
          sx={{
            flex: "1 1 420px",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Typography sx={enterpriseSectionTitleSx}>
            Filter Documents
          </Typography>

          <Box
            onDrop={(e) => handleRcDrop(e, "excel")}
            onDragOver={handleDragOver}
            sx={getEnterpriseUploadZoneSx(false)}
          >
            <Typography sx={{ ...enterpriseBodyTextSx, mb: 0.5, wordBreak: "break-all" }}>
              {excelFile ? excelFile.name : "Drag and drop file here"}
            </Typography>
            <Typography sx={enterpriseSupportingTextSx}>
              (Supported: {FILTER_UPLOAD_FORMAT_LABEL})
            </Typography>

            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
              <Button
                variant="contained"
                size="medium"
                sx={enterpriseBrowseButtonSx}
                onClick={() => excelInputRef.current.click()}
              >
                Browse
              </Button>

              {excelFile && (
                <>
                  <Button
                    variant="outlined"
                    size="medium"
                    sx={enterpriseGhostButtonSx}
                    onClick={() => setShowExcelPreview((prev) => !prev)}
                  >
                    {showExcelPreview ? "Hide Preview" : "Preview"}
                  </Button>

                  <Button
                    variant="outlined"
                    size="medium"
                    sx={enterpriseDangerButtonSx}
                    onClick={() => handleClearFile("excel")}
                  >
                    Clear
                  </Button>
                </>
              )}
            </Box>

            <input
              type="file"
              accept={FILTER_UPLOAD_ACCEPT}
              ref={excelInputRef}
              style={{ display: "none" }}
              onChange={(e) => handleFileChange(e, "excel")}
            />

            {/* === PDF Preview Section === */}
            {showExcelPreview && excelFile && (
              <Box
                sx={{
                  mt: 2,
                  p: 1,
                  borderRadius: 2,
                  border: "1px solid #e2e8f0",
                  backgroundColor: "white",
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: "#075b97",
                    fontWeight: 600,
                    mb: 1,
                    fontSize: 14,
                    textAlign: "left",
                  }}
                >
                  Excel Preview:
                </Typography>

                <iframe
                  src={URL.createObjectURL(excelFile)}
                  title="PDF Preview"
                  width="100%"
                  height="250px"
                  style={{
                    borderRadius: "8px",
                    border: "1px solid #e2e8f0",
                  }}
                />
              </Box>
            )}
          </Box>
          {/* Download Sample Excel */}
          {sampleFilterDocumentExcel && (
            <a
              href={sampleFilterDocumentExcel}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: "#0000ffac",
                fontSize: "1.4rem",
                marginTop: "1rem",
                cursor: "pointer",
                fontFamily: "gilroyMedium",
                textDecoration: "underline",
                display: "inline-block",
              }}
            >
              Sample Filter CSV
            </a>
          )}
        </Box>
      </Box>

      <Box sx={{ display: "flex", gap: 2 }}>
        {/* ===== Upload Button ===== */}
        <Button
          variant="contained"
          sx={{ ...enterprisePrimaryButtonSx, mt: 3, opacity: isUploadDisabled ? 0.7 : 1 }}
          onClick={handleRCApi}
          disabled={isUploadDisabled}
        >
          {loading ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            "Upload and Extract"
          )}
        </Button>

        {/* ✅ Show Save button after upload is done */}
        {isUploadDone && (
          <Button
            variant="contained"
            color="primary"
            sx={{
              fontFamily: "gilroyBold",
              textTransform: "none",
              
              fontSize: "14px",
              borderRadius: 1,
              px: 2.5,
              py: 1,
              mt: 3,
            }}
            onClick={handleSave}
          >
            Save
          </Button>
        )}
      </Box>

      {/* ===== Extracted Data Table ===== */}
      {extractedData && (
        <Box sx={{ mt: 4 }}>
          <Typography
            variant="h5"
            sx={{ fontWeight: 600, color: "#075b97", mb: 2, fontSize: "16px" }}
          >
            Extracted Purchase Order Details
          </Typography>

          {/* PO Summary */}
          <Box
            sx={{
              mb: 2,
              p: 2,
              borderRadius: 2,
              backgroundColor: "#f8fafc",
              border: "1px solid #e2e8f0",
            }}
          >
            <Typography
              sx={{ mb: 0.5, fontSize: "14px", fontFamily: "GilroyMedium" }}
            >
              <strong>PO No:</strong>{" "}
              {extractedData.header_info.purchase_order_no}
            </Typography>
            <Typography
              sx={{ fontSize: "14px", fontFamily: "GilroyMedium", mb: 0.5 }}
            >
              <strong>PO Date:</strong>{" "}
              {extractedData.header_info.purchase_order_date}
            </Typography>
            <Typography sx={{ fontSize: "14px", fontFamily: "GilroyMedium" }}>
              <strong>Supplier:</strong>{" "}
              {extractedData.header_info.vendor_details.split("\n")[0]}
            </Typography>
          </Box>

          {/* Data Table */}
          <TableContainer
            component={Paper}
            sx={{
              borderRadius: 2,
              boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
            }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {[
                    // "Sr. No.",
                    "Part No",
                    "Description of Goods",
                    "HSN Code",
                    "Quantity.",
                    "Unit of Measure",
                    "Rate",
                    "CGST Rate",
                    "SGST Rate",
                    "IGST Rate",
                    "Amount",
                  ].map((header, index) => (
                    <TableCell
                      key={index}
                      sx={{
                        fontWeight: 600,
                        fontFamily: "GilroySemiBold",
                        backgroundColor: "#e0e9ee",
                        fontSize: "14px",
                        borderBottom: "1px solid #e2e8f0",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {extractedData.data.map((item, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      backgroundColor: "white",
                      "&:hover": { backgroundColor: "#f5f5f5" },
                      borderBottom: "1px solid #e2e8f0",
                    }}
                  >
                    {/* <TableCell sx={ { fontSize: "14px", fontFamily: "GilroyMedium" } }>{ item["Sr. No."] }</TableCell> */}
                    <TableCell
                      sx={{ fontSize: "14px", fontFamily: "GilroyMedium" }}
                    >
                      {item["Part No"]}
                    </TableCell>
                    <TableCell
                      sx={{ fontSize: "14px", fontFamily: "GilroyMedium" }}
                    >
                      {item["Description of Goods"]}
                    </TableCell>
                    <TableCell
                      sx={{ fontSize: "14px", fontFamily: "GilroyMedium" }}
                    >
                      {item["HSN Code"]}
                    </TableCell>
                    <TableCell
                      sx={{ fontSize: "14px", fontFamily: "GilroyMedium" }}
                    >
                      {item["Qty."]}
                    </TableCell>
                    <TableCell
                      sx={{ fontSize: "14px", fontFamily: "GilroyMedium" }}
                    >
                      {item["UOM"]}
                    </TableCell>
                    <TableCell
                      sx={{ fontSize: "14px", fontFamily: "GilroyMedium" }}
                    >
                      ₹{Number(item["Rate"]).toLocaleString("en-IN")}
                    </TableCell>
                    <TableCell
                      sx={{ fontSize: "14px", fontFamily: "GilroyMedium" }}
                    >
                      {item["CGST Rate"]}%
                    </TableCell>
                    <TableCell
                      sx={{ fontSize: "14px", fontFamily: "GilroyMedium" }}
                    >
                      {item["SGST Rate"]}%
                    </TableCell>
                    <TableCell
                      sx={{ fontSize: "14px", fontFamily: "GilroyMedium" }}
                    >
                      {item["IGST Rate"]}%
                    </TableCell>
                    <TableCell
                      sx={{
                        color: "#075b97",
                        fontWeight: 600,
                        fontSize: "14px",
                        fontFamily: "GilroyMedium",
                      }}
                    >
                      ₹{Number(item["Amount"]).toLocaleString("en-IN")}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 2,
            }}
          >
            {/* Totals */}
            <Box sx={{}}>
              <Typography sx={{ fontSize: "16px", fontFamily: "GilroyMedium" }}>
                <strong>Total:</strong> ₹
                {Number(extractedData.totals.total).toLocaleString("en-IN")}
              </Typography>
              <Typography sx={{ fontSize: "16px", fontFamily: "GilroyMedium" }}>
                <strong>Taxes Total:</strong> ₹
                {Number(extractedData.totals.taxes_total).toLocaleString(
                  "en-IN"
                )}
              </Typography>
              <Typography
                sx={{
                  fontWeight: 700,
                  color: "#075b97",
                  fontSize: "16px",
                  fontFamily: "GilroyMedium",
                }}
              >
                <strong>Grand Total:</strong> ₹
                {Number(extractedData.totals.grand_total).toLocaleString(
                  "en-IN"
                )}
              </Typography>
            </Box>

            {/* Download Button */}
            <Box>
              <Button
                variant="contained"
                color="primary"
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: 1.5,
                  fontSize: "14px",
                  px: 2.5,
                  py: 1,
                }}
                onClick={handleDownloadPO}
              >
                Download PO
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default RcCreatePage;
